import React, { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import styled, { keyframes } from "styled-components";
import { properties } from "../../../../../data/successCases";
import useSelectedLanguageCode from "../../../../../hooks/useSelectedLanguageCode";
import Reveal from "../../../../../components/LandingPage/v2/Reveal";
import {
	SectionV2,
	InnerV2,
	HeadV2,
	PillButton,
	accents,
} from "../../../../../components/LandingPage/v2/primitives";

const ROTATION_MS = 11000;

// node coordinates inside the 800x700 network viewBox, one per client
const NODE_POS = [
	{ x: 14, y: 18 },
	{ x: 50, y: 8 },
	{ x: 86, y: 18 },
	{ x: 94, y: 55 },
	{ x: 76, y: 88 },
	{ x: 26, y: 90 },
	{ x: 6, y: 55 },
];

const CENTER = { x: 50, y: 50 };
const toPx = (p) => ({ x: (p.x / 100) * 800, y: (p.y / 100) * 700 });

// geometry in viewBox units — chips and logo are sized as a percentage of
// the map so this holds at any rendered size, letting every line and packet
// start/stop exactly at the element edges. The bare hub logo is wider than
// tall, so its boundary is an ellipse rather than a circle.
const NODE_R = 36;
// the active chip scales up (1.3x) for logo readability; its edge moves out
const NODE_R_ACTIVE = 47;
const HUB_RX = 60;
const HUB_RY = 46;
const EDGE_GAP = 3;

const EDGES = NODE_POS.map((pos, i) => {
	const n = toPx(pos);
	const c = toPx(CENTER);
	const dx = c.x - n.x;
	const dy = c.y - n.y;
	const d = Math.hypot(dx, dy);
	const ux = dx / d;
	const uy = dy / d;
	// ellipse radius along this direction
	const hubR =
		(HUB_RX * HUB_RY) / Math.hypot(HUB_RY * ux, HUB_RX * uy) + EDGE_GAP;
	// each edge arcs gently instead of running straight: the quadratic
	// control point sits off the midline, alternating sides around the ring
	const bend = 30 * (i % 2 === 0 ? 1 : -1);
	return {
		ux,
		uy,
		cpx: (n.x + c.x) / 2 - uy * bend,
		cpy: (n.y + c.y) / 2 + ux * bend,
		sx: n.x + ux * (NODE_R + EDGE_GAP),
		sy: n.y + uy * (NODE_R + EDGE_GAP),
		sxActive: n.x + ux * (NODE_R_ACTIVE + EDGE_GAP),
		syActive: n.y + uy * (NODE_R_ACTIVE + EDGE_GAP),
		ex: c.x - ux * hubR,
		ey: c.y - uy * hubR,
	};
});

const barGrow = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`;

function SuccessCases() {
	const router = useRouter();
	const { t } = useTranslation();
	const lang = useSelectedLanguageCode();
	const [active, setActive] = useState(0);
	const [paused, setPaused] = useState(false);
	const remainingRef = useRef(ROTATION_MS);
	const startedAtRef = useRef(0);

	const client = properties[active];

	// each case gets a fresh clock when it takes the stage
	useEffect(() => {
		remainingRef.current = ROTATION_MS;
	}, [active]);

	// auto-advance through the network; hovering the active node holds the case
	// on screen (progress bar frozen) and the clock resumes where it stopped
	useEffect(() => {
		if (paused) return undefined;
		startedAtRef.current = Date.now();
		const id = setTimeout(
			() => setActive((current) => (current + 1) % properties.length),
			remainingRef.current,
		);
		return () => {
			clearTimeout(id);
			remainingRef.current = Math.max(
				500,
				remainingRef.current - (Date.now() - startedAtRef.current),
			);
		};
	}, [active, paused]);

	// touch has no hover: a tap must not freeze the carousel with no way out
	const holdRotation = (on) => {
		if (
			typeof window !== "undefined" &&
			window.matchMedia("(hover: hover)").matches
		)
			setPaused(on);
	};

	const openCase = (caseId) => {
		router.push(`/${router.query.locale}/cases/${caseId}`);
	};

	const title = t("cases.title");
	const titleWords = title.split(" ");
	const lastWord = titleWords.pop();

	return (
		<SectionV2 id="cases" $accent={accents.violet} $glowAt="50% 8%">
			<InnerV2>
				<Reveal>
					<HeadV2 $accent={accents.violet}>
						<span className="overline">Cases</span>
						<h2>
							{titleWords.join(" ")} <em>{lastWord}</em>
						</h2>
						<p>{t("cases.subtitle")}</p>
					</HeadV2>
				</Reveal>

				<NetworkLayout>
					<Reveal>
						<NetworkMap aria-label="Rede de clientes Verzel">
							<svg viewBox="0 0 800 700" aria-hidden="true">
								{properties.map((c, i) => {
									const edge = EDGES[i];
									const isActive = i === active;
									const sx = isActive ? edge.sxActive : edge.sx;
									const sy = isActive ? edge.syActive : edge.sy;
									const inBegin = `${i * 0.45}s`;
									const outBegin = `${i * 0.45 + 1.6}s`;
									const dIn = `M ${sx} ${sy} Q ${edge.cpx} ${edge.cpy} ${edge.ex} ${edge.ey}`;
									const dOut = `M ${edge.ex} ${edge.ey} Q ${edge.cpx} ${edge.cpy} ${sx} ${sy}`;
									return (
										/* remounting on active flip restarts the SMIL packets
										   against the recomputed endpoints */
										<g key={`${c.id}-${isActive}`}>
											<path
												className={isActive ? "flow" : undefined}
												d={dIn}
												fill="none"
												stroke={isActive ? c.clientColor : "#ffffff"}
												strokeOpacity={isActive ? 0.5 : 0.16}
												strokeWidth={isActive ? 1.6 : 1.1}
												strokeLinecap="round"
												strokeDasharray={isActive ? "7 6" : "2.5 9"}
											/>
											{/* feedback loop: client data flows into the hub... */}
											<circle
												className="packet"
												r={isActive ? 3.6 : 2.2}
												fill="#ffffff"
												opacity="0"
											>
												<animateMotion
													path={dIn}
													dur="3.2s"
													begin={inBegin}
													repeatCount="indefinite"
												/>
												<animate
													attributeName="opacity"
													values={isActive ? "0;0.9;0.9;0" : "0;0.45;0.45;0"}
													keyTimes="0;0.15;0.8;1"
													dur="3.2s"
													begin={inBegin}
													repeatCount="indefinite"
												/>
											</circle>
											{/* ...and Verzel technology flows back to the client */}
											<circle
												className="packet"
												r={isActive ? 3 : 1.9}
												fill="#B7B0FF"
												opacity="0"
											>
												<animateMotion
													path={dOut}
													dur="3.2s"
													begin={outBegin}
													repeatCount="indefinite"
												/>
												<animate
													attributeName="opacity"
													values={isActive ? "0;0.8;0.8;0" : "0;0.4;0.4;0"}
													keyTimes="0;0.15;0.8;1"
													dur="3.2s"
													begin={outBegin}
													repeatCount="indefinite"
												/>
											</circle>
										</g>
									);
								})}
							</svg>

							<CenterNode aria-hidden="true">
								<img
									src="/landings/assets/images/logo-verzel-symbol-white.svg"
									alt=""
								/>
							</CenterNode>

							{properties.map((c, i) => (
								<NodeFloat
									key={c.id}
									$x={NODE_POS[i].x}
									$y={NODE_POS[i].y}
									$i={i}
								>
									<NodeButton
										type="button"
										$color={c.clientColor}
										$active={i === active}
										$i={i}
										$ux={EDGES[i].ux}
										$uy={EDGES[i].uy}
										onClick={() => setActive(i)}
										onMouseEnter={
											i === active ? () => holdRotation(true) : undefined
										}
										onMouseLeave={
											i === active ? () => holdRotation(false) : undefined
										}
										aria-label={c.clientName[lang]}
										aria-pressed={i === active}
										title={c.clientName[lang]}
									>
										<img src={c.clientLogo} alt="" loading="lazy" />
									</NodeButton>
								</NodeFloat>
							))}
						</NetworkMap>
					</Reveal>

					<Reveal delay={140}>
						<TerminalCard key={client.id} $color={client.clientColor}>
							<div className="bar">
								<span className="dots" aria-hidden="true">
									<i />
									<i />
									<i />
								</span>
								<span className="path">
									<b>{client.clientName[lang]}</b>
								</span>
								<span className="segment">{client.clientSegment[lang]}</span>
							</div>
							<div
								className="media"
								style={{ backgroundImage: `url(${client.backgroundImg})` }}
								aria-hidden="true"
							/>
							<div className="body">
								<h3>{client.clientName[lang]}</h3>
								<p>{client.disclaimer[lang]}</p>
								<div className="stats">
									{client.data.slice(0, 3).map((stat, i) => (
										<div className="stat" key={i}>
											<strong>{stat.number}</strong>
											<span>{stat.text[lang]}</span>
										</div>
									))}
								</div>
								<div className="progress" aria-hidden="true">
									<i
										key={active}
										style={{
											background: client.clientColor,
											boxShadow: `0 0 8px ${client.clientColor}99`,
											animationPlayState: paused ? "paused" : "running",
										}}
									/>
								</div>
								<PillButton
									$accent={accents.violet}
									onClick={() => openCase(client.id)}
								>
									{t("cases.button")}
									<svg
										width="15"
										height="15"
										viewBox="0 0 16 16"
										fill="none"
										aria-hidden="true"
									>
										<path
											d="M3 8h10M9 4l4 4-4 4"
											stroke="currentColor"
											strokeWidth="1.6"
											strokeLinecap="round"
											strokeLinejoin="round"
										/>
									</svg>
								</PillButton>
							</div>
							<span className="scanline" aria-hidden="true" />
						</TerminalCard>
					</Reveal>
				</NetworkLayout>
			</InnerV2>
		</SectionV2>
	);
}

/* the depth layers repeat in every frame so the pulse (which owns
   box-shadow while active) doesn't wipe the chip's static shadow */
const nodePulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 var(--pulse-color),
      0 10px 26px rgba(5, 4, 18, 0.45),
      inset 0 1px 0 rgba(255, 255, 255, 0.08);
  }
  70% {
    box-shadow: 0 0 0 14px transparent,
      0 10px 26px rgba(5, 4, 18, 0.45),
      inset 0 1px 0 rgba(255, 255, 255, 0.08);
  }
  100% {
    box-shadow: 0 0 0 0 transparent,
      0 10px 26px rgba(5, 4, 18, 0.45),
      inset 0 1px 0 rgba(255, 255, 255, 0.08);
  }
`;

const hubGlow = keyframes`
  0%, 100% {
    filter: drop-shadow(0 0 12px #7d72ff59) drop-shadow(0 0 32px #7d72ff26);
  }
  50% {
    filter: drop-shadow(0 0 20px #7d72ff8c) drop-shadow(0 0 48px #7d72ff40);
  }
`;

const materialize = keyframes`
  from {
    opacity: 0;
    clip-path: inset(0 0 100% 0);
  }
  to {
    opacity: 1;
    clip-path: inset(0 0 0% 0);
  }
`;

const scan = keyframes`
  from { top: 0; opacity: 1; }
  85% { opacity: 1; }
  to { top: 100%; opacity: 0; }
`;

/* -26 = 2x the active dash period (7 + 6), so the loop is seamless;
   decreasing offset slides the dashes toward the hub */
const dashFlow = keyframes`
  to { stroke-dashoffset: -26; }
`;

const NetworkLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 36px;
  align-items: center;

  @media (min-width: 992px) {
    grid-template-columns: 1.15fr 1fr;
    gap: 48px;
  }
`;

const NetworkMap = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 8 / 7;
  max-width: 640px;
  margin: 0 auto;

  svg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
  }

  path.flow {
    animation: ${dashFlow} 1.4s linear infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    .packet {
      display: none;
    }

    path.flow {
      animation: none;
    }
  }
`;

/* bare glowing brand mark as the hub: width = 2 * HUB_RX / 800 of the map,
   so the SVG line endpoints hug the logo's edge */
const CenterNode = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 15%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${hubGlow} 3.6s ease-in-out infinite;

  /* the symbol svg ships with preserveAspectRatio="none", so it needs a box
     with the artwork's own 140:102.666 ratio or it stretches */
  img {
    width: 100%;
    aspect-ratio: 140 / 102.666;
    height: auto;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/* each node breathes along its own connection line (toward/away from the
   hub), so the line tip never detaches — a vertical drift would swing the
   side nodes perpendicular to their nearly-horizontal lines */
const nodeFloat = (ux, uy) => keyframes`
  from {
    transform: translate(${(-ux * 4).toFixed(2)}px, ${(-uy * 4).toFixed(2)}px)
      scale(var(--s));
  }
  to {
    transform: translate(${(ux * 4).toFixed(2)}px, ${(uy * 4).toFixed(2)}px)
      scale(var(--s));
  }
`;

/* node diameter = 2 * NODE_R / 800 of the map width (same contract as the
   hub); the wrapper owns the anchor position, the button drifts inside it */
const NodeFloat = styled.div`
  position: absolute;
  left: ${(p) => p.$x}%;
  top: ${(p) => p.$y}%;
  transform: translate(-50%, -50%);
  width: 9%;
  aspect-ratio: 1;
`;

const NodeButton = styled.button`
  --pulse-color: ${(p) => p.$color + "59"};
  --s: ${(p) => (p.$active ? 1.3 : 1)};
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  padding: 17%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background:
    radial-gradient(120% 120% at 30% 20%, rgba(255, 255, 255, 0.12), rgba(255, 255, 255, 0.02) 55%),
    rgba(16, 12, 38, 0.6);
  border: 1.5px solid
    ${(p) => (p.$active ? p.$color : "rgba(255, 255, 255, 0.22)")};
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  box-shadow:
    0 10px 26px rgba(5, 4, 18, 0.45),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);
  transition: border-color 0.35s ease;

  /* faint outer ring lends the chip weight against the section glow */
  &::before {
    content: "";
    position: absolute;
    inset: -10%;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.08);
    pointer-events: none;
  }
  animation:
    ${(p) => nodeFloat(p.$ux, p.$uy)} ${(p) => 4.6 + (p.$i % 3) * 0.9}s
      ease-in-out ${(p) => -p.$i * 1.15}s infinite alternate,
    ${(p) => (p.$active ? nodePulse : "none")} 2s ease-out infinite;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    filter: ${(p) => (p.$active ? "none" : "grayscale(1) brightness(1.6)")};
    opacity: ${(p) => (p.$active ? 1 : 0.75)};
    transition: filter 0.35s ease, opacity 0.35s ease;
  }

  &:hover {
    border-color: ${(p) => p.$color};

    img {
      filter: none;
      opacity: 1;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: none;
  }
`;

const TerminalCard = styled.article`
  position: relative;
  overflow: hidden;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: linear-gradient(170deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.015));
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  animation: ${materialize} 0.65s cubic-bezier(0.16, 1, 0.3, 1) both;

  .scanline {
    position: absolute;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, ${(p) => p.$color}cc, transparent);
    animation: ${scan} 0.65s cubic-bezier(0.16, 1, 0.3, 1) both;
    pointer-events: none;
  }

  .bar {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 18px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    font-family: "Hind Madurai";
    font-size: 12px;
    letter-spacing: 0.6px;
    color: #8b93a1;

    .dots {
      display: flex;
      gap: 5px;

      i {
        width: 9px;
        height: 9px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.14);

        &:first-child {
          background: ${(p) => p.$color}aa;
        }
      }
    }

    .path {
      flex: 1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      b {
        color: #d7dbe2;
        font-weight: 600;
      }
    }

    .segment {
      padding: 3px 12px;
      border-radius: 100px;
      border: 1px solid ${(p) => p.$color}59;
      color: ${(p) => p.$color};
      font-weight: 600;
      white-space: nowrap;
    }
  }

  .media {
    height: 132px;
    background-size: cover;
    /* privilege the top of the photo (faces/subject) — center cropped the
       heads; the bottom already fades under the mask below */
    background-position: 50% 0%;
    mask-image: linear-gradient(180deg, black 30%, transparent 100%);
    -webkit-mask-image: linear-gradient(180deg, black 30%, transparent 100%);
  }

  .body {
    padding: 0 26px 26px 26px;
    margin-top: -24px;
    position: relative;

    h3 {
      font-family: "Hind Madurai";
      font-size: clamp(1.6rem, 2.4vw, 2.2rem);
      font-weight: 600;
      color: #ffffff;
      margin: 0 0 10px 0;
    }

    > p {
      font-family: "Hind Madurai";
      font-size: 0.95rem;
      line-height: 1.65;
      color: #a9afb8;
      margin: 0 0 20px 0;
      min-height: 76px;
    }

    .stats {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 14px;
      margin-bottom: 16px;
      /* every cell stretches to the row height (equal side bars); the
         content centers vertically inside it */
      align-items: stretch;

      .stat {
        display: flex;
        flex-direction: column;
        justify-content: center;
        border-left: 2px solid ${(p) => p.$color}73;
        padding-left: 10px;

        strong {
          display: block;
          font-family: "Hind Madurai";
          font-size: 1.25rem;
          font-weight: 700;
          color: ${(p) => p.$color};
          line-height: 1.2;
        }

        span {
          font-family: "Hind Madurai";
          font-size: 0.72rem;
          line-height: 1.45;
          color: #8b93a1;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      }
    }

    /* thin, discrete countdown to the next case, tinted with the case color */
    .progress {
      height: 2px;
      border-radius: 2px;
      background: rgba(255, 255, 255, 0.08);
      overflow: hidden;
      margin: 0 0 20px 0;

      i {
        display: block;
        height: 100%;
        width: 100%;
        transform-origin: left center;
        transform: scaleX(0);
        border-radius: inherit;
        animation: ${barGrow} ${ROTATION_MS}ms linear forwards;
      }
    }
  }

  @media (max-width: 991px) {
    .body > p {
      min-height: 0;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;

    .scanline,
    .progress {
      display: none;
    }
  }
`;

export default SuccessCases;
